import React from 'react';
import { UserCheck, Search, CalendarCheck, Star, ArrowRight } from 'lucide-react';

const fonts = {
  serif: "'Playfair Display', Georgia, serif",
  sans:  "'Outfit', system-ui, sans-serif",
};

const steps = [
  {
    icon:    UserCheck,
    step:    '01',
    title:   'Create Your Account',
    desc:    'Sign up as a tourism entrepreneur or expert. Free to join, no hidden fees.',
    color:   '#15803d',   // forest green
    light:   '#dcfce7',
    accent:  '#4ade80',
    shadow:  'rgba(21,128,61,0.18)',
  },
  {
    icon:    Search,
    step:    '02',
    title:   'Browse & Filter Experts',
    desc:    'Find specialists by level, location, specialty or rating. All profiles are verified.',
    color:   '#b45309',   // amber
    light:   '#fef3c7',
    accent:  '#fbbf24',
    shadow:  'rgba(180,83,9,0.18)',
  },
  {
    icon:    CalendarCheck,
    step:    '03',
    title:   'Book a Session',
    desc:    'Choose your session type — video, audio or chat. Pick a time that suits you both.',
    color:   '#1d4ed8',   // blue
    light:   '#dbeafe',
    accent:  '#60a5fa',
    shadow:  'rgba(29,78,216,0.18)',
  },
  {
    icon:    Star,
    step:    '04',
    title:   'Grow Your Business',
    desc:    'Complete levels, submit your canvas, and unlock the next chapter of your journey.',
    color:   '#9333ea',   // purple
    light:   '#f3e8ff',
    accent:  '#c084fc',
    shadow:  'rgba(147,51,234,0.18)',
  },
];

const StepCard = ({ icon: Icon, step, title, desc, color, light, accent, shadow, isLast }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      style={{
        position:   'relative',
        background: hovered ? light : '#ffffff',
        border:     `1.5px solid ${hovered ? accent : '#e8e3d9'}`,
        borderRadius: 18,
        padding:    '28px 24px 24px',
        transition: 'all 0.25s ease',
        cursor:     'default',
        boxShadow:  hovered ? `0 12px 40px ${shadow}` : '0 1px 4px rgba(0,0,0,0.04)',
        transform:  hovered ? 'translateY(-4px)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Coloured top bar */}
      <div style={{
        position:     'absolute',
        top: 0, left: 0, right: 0,
        height:       3,
        borderRadius: '18px 18px 0 0',
        background:   `linear-gradient(90deg, ${color}, ${accent})`,
        opacity:      hovered ? 1 : 0.35,
        transition:   'opacity 0.25s ease',
      }} />

      {/* Icon + step number row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background:  hovered ? color : light,
          display:     'flex',
          alignItems:  'center',
          justifyContent: 'center',
          transition:  'background 0.25s ease',
          flexShrink:  0,
          boxShadow:   hovered ? `0 4px 16px ${shadow}` : 'none',
        }}>
          <Icon size={22} color={hovered ? '#fff' : color} strokeWidth={1.8} />
        </div>

        <span style={{
          fontFamily: fonts.serif,
          fontSize:   40,
          fontWeight: 700,
          color:      hovered ? accent : '#e8e3d9',
          lineHeight: 1,
          transition: 'color 0.25s ease',
          userSelect: 'none',
        }}>
          {step}
        </span>
      </div>

      <h3 style={{
        fontFamily: fonts.serif,
        fontSize:   17,
        fontWeight: 700,
        color:      hovered ? color : '#1a2e1a',
        margin:     '0 0 8px',
        lineHeight: 1.3,
        transition: 'color 0.25s ease',
      }}>
        {title}
      </h3>

      <p style={{
        fontFamily: fonts.sans,
        fontSize:   13.5,
        color:      '#666',
        lineHeight: 1.75,
        margin:     0,
      }}>
        {desc}
      </p>

      {/* Arrow indicator on hover */}
      <div style={{
        marginTop:  16,
        display:    'flex',
        alignItems: 'center',
        gap:        6,
        opacity:    hovered ? 1 : 0,
        transform:  hovered ? 'translateX(0)' : 'translateX(-6px)',
        transition: 'all 0.25s ease',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color, fontFamily: fonts.sans }}>Learn more</span>
        <ArrowRight size={12} color={color} />
      </div>
    </div>
  );
};

const HowItWorks = () => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=Outfit:wght@400;500;600&display=swap');
    `}</style>

    <section id="how-it-works" style={{ background: '#f8f6f0', fontFamily: fonts.sans, padding: '96px 32px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{
            display:        'inline-flex',
            alignItems:     'center',
            gap:            8,
            fontSize:       11,
            fontWeight:     600,
            letterSpacing:  '0.1em',
            textTransform:  'uppercase',
            color:          '#3b6d11',
            marginBottom:   16,
          }}>
            <span style={{ width: 24, height: 1.5, background: '#3b6d11', display: 'inline-block', borderRadius: 2 }} />
            How It Works
            <span style={{ width: 24, height: 1.5, background: '#3b6d11', display: 'inline-block', borderRadius: 2 }} />
          </div>

          <h2 style={{
            fontFamily:    fonts.serif,
            fontSize:      'clamp(32px, 5vw, 44px)',
            fontWeight:    700,
            color:         '#1a2e1a',
            lineHeight:    1.1,
            margin:        '0 auto',
            maxWidth:      480,
            letterSpacing: '-0.02em',
          }}>
            Your path to {' '}
            <span style={{ fontWeight: 400, fontStyle: 'italic', color: '#3b6d11' }}>
              success
            </span>
          </h2>

          <p style={{ fontSize: 15, color: '#777', marginTop: 14, maxWidth: 420, margin: '14px auto 0', lineHeight: 1.7, fontFamily: fonts.sans }}>
            Four simple steps to connect with the right expert and grow your tourism cultural, and creative arts enterprises with experts.
          </p>
        </div>

        {/* Steps grid */}
        <div style={{
          display:               'grid',
          gridTemplateColumns:   'repeat(auto-fit, minmax(230px, 1fr))',
          gap:                   20,
        }}>
          {steps.map((s, i) => (
            <StepCard key={i} {...s} isLast={i === steps.length - 1} />
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: 56 }}>
          <a href="/register" style={{
            display:        'inline-flex',
            alignItems:     'center',
            gap:            8,
            fontFamily:     fonts.sans,
            fontSize:       14,
            fontWeight:     600,
            color:          '#fff',
            textDecoration: 'none',
            padding:        '14px 30px',
            borderRadius:   12,
            background:     'linear-gradient(135deg, #15803d, #22c55e)',
            boxShadow:      '0 4px 20px rgba(21,128,61,0.3)',
            transition:     'transform 0.2s, box-shadow 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(21,128,61,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = '0 4px 20px rgba(21,128,61,0.3)'; }}
          >
            Get Started Free <ArrowRight size={15} />
          </a>
          {/* <p style={{ fontSize: 12, color: '#aaa', marginTop: 10, fontFamily: fonts.sans }}>No credit card required</p> */}
        </div>
      </div>
    </section>
  </>
);

export default HowItWorks;